import React from 'react';
import Relay from 'react-relay';
import { defineMessages, injectIntl, FormattedMessage, FormattedDate } from 'react-intl';
import { Link } from 'react-router';
import { fromGlobalId } from 'graphql-relay';
import urlMapping from '../app/URLMapping';
import { TEST_STATUS_PASSED, TEST_STATUS_FAILED, TEST_STATUS_PENDING, TestInvocation } from '../../common/types/TestTypes';
import RejectTestRunMutation from './RejectTestRunMutation';
import { notification } from './notification';
import { getPayloadErrorMessage } from './mutationPayload';
import './TestResultsTable.scss';

const uuidRegex = /[0-9a-fA-F\-]{30,40}/;

const messages = defineMessages({
    rerunFailed: {
        id: 'snapshotTesting.rerunFailed',
        defaultMessage: 'Failed to rerun test invocation. Please try again.',
    },
    rejectRunFailed: {
        id: 'snapshotTesting.rejectRunFailed',
        defaultMessage: 'Failed to reject test run. Please try again.',
    },
});

function normalizeSnapshotEntityId(id?: string | { id?: string | null } | null): string | null {
    if (!id) {
        return null;
    }

    if (typeof id === 'object') {
        return normalizeSnapshotEntityId(id.id);
    }

    if (uuidRegex.test(id)) {
        return id;
    }

    try {
        const globalId = fromGlobalId(id);
        return globalId.id || id;
    } catch (e) {
        return id;
    }
}

function getPreferredSnapshotEntityId(entity?: { id?: string | { id?: string | null } | null; realId?: string | null } | null): string | null {
    if (!entity) {
        return null;
    }

    if (entity.realId) {
        return entity.realId;
    }

    return normalizeSnapshotEntityId(entity.id);
}

function getInvocationRunsUrl(invocation: TestInvocation) {
    return urlMapping.getUrl('snapshotTesting', 'runs', {
        organisation_id: invocation.organisation_id,
        flow_id: invocation.flow_id,
        id: getPreferredSnapshotEntityId(invocation),
    });
}

function getRunUrl(invocation: TestInvocation, run?: { id?: string | null; realId?: string | null } | null) {
    return urlMapping.getUrl('snapshotTesting', 'validateRun', {
        organisation_id: invocation.organisation_id,
        flow_id: invocation.flow_id,
        id: getPreferredSnapshotEntityId(invocation),
        runId: getPreferredSnapshotEntityId(run),
    });
}

interface RunTestInvocationMutationProps {
    testInvocationId: string;
}

class RunTestInvocationMutation extends Relay.Mutation<RunTestInvocationMutationProps, any> {
    private mutationProps: RunTestInvocationMutationProps;

    constructor(props: RunTestInvocationMutationProps) {
        super(props as any);
        this.mutationProps = props;
    }

    getMutation() {
        return Relay.QL`mutation {runTestInvocation}`;
    }

    getFatQuery() {
        return Relay.QL`
            fragment on RunTestInvocationPayload {
                job {
                    id
                    realId
                    organisation_id
                    status
                    progress
                }
                success
                error
            }
        `;
    }

    getVariables() {
        return {
            testInvocationId: this.mutationProps.testInvocationId,
        };
    }

    getConfigs() {
        return [{
            type: 'REQUIRED_CHILDREN',
            children: [this.getFatQuery()],
        }];
    }
}

interface TestResultsTableProps {
    invocations: TestInvocation[];
    onRerun?: (invocationId: string) => void;
}

interface TestResultsTableState {
    expandedRows: Set<string>;
}

export class TestResultsTable extends React.Component<TestResultsTableProps & { intl: any }, TestResultsTableState> {

    constructor(props: TestResultsTableProps & { intl: any }) {
        super(props);
        this.state = {
            expandedRows: new Set()
        };
    }

    toggleRow = (id: string) => {
        this.setState(prevState => {
            const newExpandedRows = new Set(prevState.expandedRows);
            if (newExpandedRows.has(id)) {
                newExpandedRows.delete(id);
            } else {
                newExpandedRows.add(id);
            }
            return { expandedRows: newExpandedRows };
        });
    };

    renderStatus(status: string | undefined) {
        let labelClass = 'status-default';
        let statusText = status || 'Not run';

        if (status === TEST_STATUS_PASSED) {
            labelClass = 'status-passed';
        } else if (status === TEST_STATUS_FAILED) {
            labelClass = 'status-failed';
        } else if (status === TEST_STATUS_PENDING) {
            labelClass = 'status-pending';
        }

        return <span className={`status-label ${labelClass}`}>{statusText}</span>;
    }

    handleRerun = (invocation: TestInvocation) => {
        const invocationId = getPreferredSnapshotEntityId(invocation);
        if (!invocationId) {
            return;
        }

        Relay.Store.commitUpdate(
            new RunTestInvocationMutation({
                testInvocationId: invocationId,
            }),
            {
                onSuccess: (response: any) => {
                    const payloadError = getPayloadErrorMessage(
                        response && response.runTestInvocation,
                        this.props.intl.formatMessage(messages.rerunFailed)
                    );
                    if (payloadError) {
                        notification.error(payloadError);
                        return;
                    }
                    if (this.props.onRerun) {
                        this.props.onRerun(invocationId);
                    }
                },
                onFailure: (error: any) => {
                    console.error('Failed to rerun test invocation', error);
                    notification.error(this.props.intl.formatMessage(messages.rerunFailed));
                },
            }
        );
    };

    handleRejectRun = (invocation: TestInvocation, run: { id?: string | null; realId?: string | null } | null | undefined) => {
        if (!run) {
            return;
        }

        Relay.Store.commitUpdate(
            new RejectTestRunMutation({
                testInvocation: {
                    id: invocation.id,
                    realId: getPreferredSnapshotEntityId(invocation) || undefined,
                    version: invocation.version,
                },
                testRun: {
                    id: run.id || '',
                    realId: getPreferredSnapshotEntityId(run) || undefined,
                },
            }),
            {
                onSuccess: (response: any) => {
                    const payloadError = getPayloadErrorMessage(
                        response && response.rejectTestRun,
                        this.props.intl.formatMessage(messages.rejectRunFailed)
                    );
                    if (payloadError) {
                        notification.error(payloadError);
                        return;
                    }
                    if (this.props.onRerun) {
                        const invocationId = getPreferredSnapshotEntityId(invocation);
                        if (invocationId) {
                            this.props.onRerun(invocationId);
                        }
                    }
                },
                onFailure: (error: any) => {
                    console.error('Failed to reject test run', error);
                    notification.error(this.props.intl.formatMessage(messages.rejectRunFailed));
                },
            }
        );
    };

    render() {
        const { invocations, onRerun, intl } = this.props;
        const safeParseDate = (dateVal: string | Date | number | undefined | null) => {
            if (!dateVal) return undefined;
            let d = new Date(dateVal as any);
            if (isNaN(d.getTime()) && (typeof dateVal === "string" || typeof dateVal === "number") && !isNaN(Number(dateVal))) {
                d = new Date(Number(dateVal));
            }
            return isNaN(d.getTime()) ? undefined : d;
        };

        const { expandedRows } = this.state;

        if (!invocations || invocations.length === 0) {
            return (
                <div className="alert alert-info">
                    <FormattedMessage defaultMessage="No tests found for this flow." id="snapshotTesting.noTests" />
                </div>
            );
        }

        return (
            <div className="test-results-table">
                <table className="table table-striped table-hover">
                    <thead>
                        <tr>
                            <th><FormattedMessage defaultMessage="Name" id="snapshotTesting.column.name" /></th>
                            <th><FormattedMessage defaultMessage="Status" id="snapshotTesting.column.status" /></th>
                            <th><FormattedMessage defaultMessage="Duration" id="snapshotTesting.column.duration" /></th>
                            <th><FormattedMessage defaultMessage="Created At" id="snapshotTesting.column.createdAt" /></th>
                            <th><FormattedMessage defaultMessage="Error" id="snapshotTesting.column.error" /></th>
                            <th className="actions-column"><FormattedMessage defaultMessage="Actions" id="snapshotTesting.column.actions" /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {invocations.map((invocation) => {
                            const isExpanded = expandedRows.has(invocation.id);
                            return [
                                <tr key={invocation.id}>
                                    <td>
                                        <Link
                                            className="invocation-link"
                                            to={getInvocationRunsUrl(invocation)}
                                        >
                                            {invocation.name}
                                        </Link>
                                        {invocation.sourceFlowInvocation_id && (
                                            <div className="source-invocation-link">
                                                <Link to={urlMapping.getUrl('flowInvocations', 'view', {
                                                    organisation_id: invocation.organisation_id,
                                                    flow_id: invocation.flow_id,
                                                    id: invocation.sourceFlowInvocation_id
                                                })}>
                                                    <FormattedMessage defaultMessage="view invocation" id="snapshotTesting.viewInvocationLink" />
                                                </Link>
                                            </div>
                                        )}
                                        <div className="historical-runs-links" style={{ marginTop: '4px', fontSize: '0.85em' }}>
                                            
                                        </div>
                                    </td>
                                    <td>{this.renderStatus(invocation.lastStatus)}</td>
                                    <td>{invocation.lastDuration ? `${invocation.lastDuration} ms` : '-'}</td>
                                    <td>
                                        {invocation.lastRunAt ? (
                                            <FormattedDate
                                                day="numeric"
                                                hour="numeric"
                                                minute="numeric"
                                                month="short"
                                                value={safeParseDate(invocation.lastRunAt as any)}
                                                year="numeric"
                                            />
                                        ) : '-'}
                                    </td>
                                    <td className="text-danger" style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={invocation.lastError || ''}>
                                        {invocation.lastError || '-'}
                                    </td>
                                    <td className="actions-column">
                                        <div className="btn-group btn-group-xs">
                                            {invocation.lastSuccessfulRun && (
                                                <Link
                                                    className="btn btn-default text-success"
                                                    title={intl.formatMessage({ id: 'snapshotTesting.lastSuccessfulRun', defaultMessage: 'Last Successful Run' })}
                                                    to={getRunUrl(invocation, invocation.lastSuccessfulRun)}
                                                >
                                                    <i className="fa fa-check-circle" style={{ marginRight: '4px' }} />
                                                    <FormattedMessage defaultMessage="Last Success" id="snapshotTesting.lastSuccessLink" />
                                                </Link>
                                            )}
                                            {invocation.lastUnsuccessfulRun && (
                                                <Link
                                                    className="btn btn-default text-danger"
                                                    title={intl.formatMessage({ id: 'snapshotTesting.lastUnsuccessfulRun', defaultMessage: 'Last Unsuccessful Run' })}
                                                    to={getRunUrl(invocation, invocation.lastUnsuccessfulRun)}
                                                >
                                                    <i className="fa fa-times-circle" style={{ marginRight: '4px' }} />
                                                    <FormattedMessage defaultMessage="Last Failure" id="snapshotTesting.lastFailureLink" />
                                                </Link>
                                            )}
                                            <Link
                                                className="btn btn-default"
                                                title={intl.formatMessage({ id: 'snapshotTesting.viewRuns', defaultMessage: 'View Runs' })}
                                                to={getInvocationRunsUrl(invocation)}
                                            >
                                                <i className="fa fa-history" style={{ marginRight: '4px' }} />
                                                <FormattedMessage defaultMessage="Runs" id="snapshotTesting.viewRuns" />
                                            </Link>
                                            {onRerun && (
                                                <button
                                                    className="btn btn-default"
                                                    onClick={() => this.handleRerun(invocation)}
                                                    title={intl.formatMessage({ id: 'snapshotTesting.rerun', defaultMessage: 'Re-run' })}
                                                >
                                                    <i className="fa fa-refresh" />
                                                </button>
                                            )}
                                            <button
                                                className="btn btn-default"
                                                onClick={() => this.toggleRow(invocation.id)}
                                                title={intl.formatMessage({ id: 'snapshotTesting.seeAll', defaultMessage: 'See All Runs' })}
                                            >
                                                <FormattedMessage defaultMessage="See All" id="snapshotTesting.seeAll" />
                                                <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ marginLeft: '5px' }} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>,
                                isExpanded && (
                                    <tr className="expanded-row" key={`${invocation.id}-expanded`}>
                                        <td colSpan={6}>
                                            <div className="runs-list">
                                                <h5><FormattedMessage defaultMessage="Recent Runs" id="snapshotTesting.recentRuns" /></h5>
                                                {invocation.runs && invocation.runs.edges && invocation.runs.edges.length > 0 ? (
                                                    <table className="table table-condensed">
                                                        <thead>
                                                            <tr>
                                                                <th><FormattedMessage defaultMessage="Status" id="snapshotTesting.column.status" /></th>
                                                                <th><FormattedMessage defaultMessage="Duration" id="snapshotTesting.column.duration" /></th>
                                                                <th><FormattedMessage defaultMessage="Created At" id="snapshotTesting.column.createdAt" /></th>
                                                                <th><FormattedMessage defaultMessage="Error" id="snapshotTesting.column.error" /></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                             {invocation.runs.edges.map(({ node: run }) => (
                                                                 <tr key={getPreferredSnapshotEntityId(run) || run.id}>
                                                                     <td>{this.renderStatus(run.status)}</td>
                                                                     <td>{run.duration ? `${run.duration} ms` : '-'}</td>
                                                                     <td>
                                                                        {run.createdAt ? (
                                                                            <FormattedDate
                                                                                day="numeric"
                                                                                hour="numeric"
                                                                                minute="numeric"
                                                                                month="short"
                                                                                value={safeParseDate(run.createdAt as any)}
                                                                                year="numeric"
                                                                            />
                                                                        ) : '-'}
                                                                     </td>
                                                                     <td className="text-danger">{run.error || '-'}</td>
                                                                     <td>
                                                                         {onRerun && (
                                                                             <button
                                                                                 className="btn btn-danger btn-xs"
                                                                                 onClick={() => this.handleRejectRun(invocation, run)}
                                                                             >
                                                                                 <FormattedMessage defaultMessage="Reject" id="snapshotTesting.reject" />
                                                                             </button>
                                                                         )}
                                                                     </td>
                                                                 </tr>
                                                             ))}
                                                         </tbody>
                                                    </table>
                                                ) : (
                                                    <p><FormattedMessage defaultMessage="No runs available." id="snapshotTesting.noRuns" /></p>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            ];
                        })}
                    </tbody>
                </table>
            </div>
        );
    }
}

export default injectIntl(TestResultsTable);
